import { useId, type ComponentType, type ReactNode } from 'react';
import { Icon } from '../atoms/Icon';
import type { IconName } from '../icons/registry';
import { useLinkComponent, type NavLinkComponentProps } from '../navigation';
import styles from './SideNav.module.css';

export interface SideNavItem {
  key: string;
  label: string;
  href: string;
  icon?: IconName;
  active?: boolean;
  /** Small counter or label on the right, e.g. open tasks. */
  badge?: ReactNode;
  /** Items with the same section are grouped below this heading. */
  section?: string;
}

export type { NavLinkComponentProps };

export interface SideNavProps {
  /** Accessible name of the navigation landmark. */
  label: string;
  items: SideNavItem[];
  /** Link component, e.g. an adapter around the router's `Link`. Defaults to `<a>`. */
  linkComponent?: ComponentType<NavLinkComponentProps>;
  /** Called after an item was chosen (e.g. to close a mobile drawer). */
  onNavigate?: () => void;
}

function groupBySection(items: SideNavItem[]) {
  const groups: { section: string | undefined; items: SideNavItem[] }[] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last && last.section === item.section) last.items.push(item);
    else groups.push({ section: item.section, items: [item] });
  }
  return groups;
}

export function SideNav({ label, items, linkComponent, onNavigate }: SideNavProps) {
  const Link = useLinkComponent(linkComponent);
  const id = useId();
  return (
    <nav aria-label={label} className={styles.nav}>
      {groupBySection(items).map((group, index) => (
        <div key={group.section ?? index} className={styles.group}>
          {group.section && (
            <span className={styles.section} id={`${id}-${index}`}>
              {group.section}
            </span>
          )}
          <ul
            className={styles.list}
            {...(group.section ? { 'aria-labelledby': `${id}-${index}` } : {})}
          >
            {group.items.map((item) => (
              <li key={item.key}>
                <Link
                  href={item.href}
                  className={[styles.link, item.active && styles.active].filter(Boolean).join(' ')}
                  {...(item.active ? { 'aria-current': 'page' as const } : {})}
                  {...(onNavigate ? { onClick: onNavigate } : {})}
                >
                  {item.icon && (
                    <span className={styles.icon}>
                      <Icon name={item.icon} size="md" />
                    </span>
                  )}
                  <span className={styles.label}>{item.label}</span>
                  {item.badge !== undefined && <span className={styles.badge}>{item.badge}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}
